//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import OnHandQuantitiesModel from './onHandQuantity.model';

/**
 * @service OnHandQuantityService
 * @description Service for managing on-hand inventory quantities and material movement registers within the Material Management System (MMS).
 * This service provides functionality to query the current stock status and historical movement of items (Material Movement Register).
 * It interacts with the backend API to retrieve calculated inventory data based on transactions like purchases, consumption, transfers, and adjustments.
 *
 * **Key Responsibilities:**
 * 1. **Material Movement Register**: Fetching detailed logs of how inventory has changed over time or for specific criteria.
 * 2. **Security/Role Management**: Sanitizing request payloads based on the logged-in user's role (e.g., preventing non-employees from querying arbitrary business IDs).
 * 3. **Resource Management**: Handling the lifecycle of HTTP requests to prevent memory leaks in the application.
 *
 * **Data Flow:**
 * - The component constructs a query object (onHandQuantity).
 * - The service checks `localStorage.loginPersonType`.
 * - If the user is not an 'EMPLOYEE', sensitive fields like `businessId` are removed to enforce context security.
 * - The request is sent to `/mms/onhandquantities/materialMovementRegister`.
 * - The response (Array of OnHandQuantitiesModel) is returned to the component.
 *
 * @feature 1. Retrieve Material Movement Register (MMR) reports.
 * @feature 2. Role-based payload sanitization for security.
 * @feature 3. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<OnHandQuantitiesModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /mms/onhandquantities/
 * @service-provider root (singleton, application-wide)
 *
 * @local-storage-keys
 * - loginPersonType: Used to determine if the user is an 'EMPLOYEE' or other role (e.g., Farmer), affecting payload sanitization.
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class OnHandQuantityService extends Service<OnHandQuantitiesModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/mms/onhandquantities/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/mms/onhandquantities/');
  }

  /**
   * @method getMmRegister
   * @async
   * @description Retrieve the Material Movement Register (MMR) based on filter criteria.
   *
   * This method fetches a list of inventory movements (stock in/out) matching the provided criteria.
   * It includes logic to sanitize the input payload based on the user's role stored in `localStorage`.
   *
   * **Security Logic:**
   * - Checks `localStorage.loginPersonType`.
   * - If the user is **NOT** an 'EMPLOYEE' (e.g., a Farmer or Dealer), the `businessId` and `businessName`
   *   properties are removed from the request object. This ensures that users can only query data
   *   relevant to their own implicitly determined context on the server, preventing unauthorized access to other businesses.
   *
   * @param {any} onHandQuantity - Filter object containing criteria like date range, item IDs, etc.
   * @returns {Promise<Array<OnHandQuantitiesModel>>} Promise resolving to an array of OnHandQuantitiesModel objects representing the register.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Modifies the `onHandQuantity` input object (deletes properties) if user is not an employee.
   * - Initiates an HTTP POST request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * const filter = { fromDate: '2023-01-01', toDate: '2023-01-31', businessId: '123' };
   * onHandQuantityService.getMmRegister(filter).then(data => {
   *   console.log('Material Movement:', data);
   * });
   */
  public getMmRegister(onHandQuantity: any) {
     if (localStorage.loginPersonType != 'EMPLOYEE') {
      delete onHandQuantity.businessId;
      delete onHandQuantity.businessName;
    }
    return new Promise<Array<OnHandQuantitiesModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<OnHandQuantitiesModel>>(`${this.baseUrl}${this.serviceUrl}materialMovementRegister`, onHandQuantity, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
